import { useEffect, useMemo, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { PoundSterling, Search, Save, Home, LineChart, X, AlertTriangle, Download, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

const _fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch (_e) { return iso || ""; }
};

// Tiny inline sparkline for a 6-month cost history. Pure SVG so no extra
// dep and it stays legible even inside a dense table row.
// Session Z60 — hover each dot to see the exact £ + date it was committed.
function Sparkline({ points, outlier, testid }) {
  const [hover, setHover] = useState(null); // {i, x, y}
  const pts = points || [];
  const vals = pts.map((p) => Number(p.value) || 0);
  if (vals.length === 0) {
    return <span className="text-[10px] italic text-slate-300">—</span>;
  }
  const w = 90;
  const h = 24;
  const pad = 3;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const stepX = vals.length > 1 ? (w - pad * 2) / (vals.length - 1) : 0;
  const coords = vals.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const path = coords
    .map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(" ");
  const first = vals[0];
  const last = vals[vals.length - 1];
  const trendUp = last > first;
  const stroke = outlier ? "#dc2626" : trendUp ? "#b45309" : "#0d7a75";
  const [lx, ly] = coords[coords.length - 1];
  const activeIdx = hover?.i ?? null;
  return (
    <span className="relative inline-block align-middle">
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        aria-label="6-month cost trend"
        onMouseLeave={() => setHover(null)}
      >
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth="1.4"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* The visible last-point dot stays even without hover. */}
        <circle cx={lx} cy={ly} r="1.8" fill={stroke} />
        {/* Invisible wider hit targets around every point so hover is forgiving. */}
        {coords.map(([x, y], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r="6"
            fill="transparent"
            data-testid={testid ? `${testid}-point-${i}` : undefined}
            onMouseEnter={() => setHover({ i, x, y })}
            style={{ cursor: "crosshair" }}
          />
        ))}
        {activeIdx !== null && (
          <circle
            cx={coords[activeIdx][0]}
            cy={coords[activeIdx][1]}
            r="2.6"
            fill="white"
            stroke={stroke}
            strokeWidth="1.4"
          />
        )}
      </svg>
      {activeIdx !== null && (
        <span
          data-testid={testid ? `${testid}-tooltip` : undefined}
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-10 z-10 bg-slate-900 text-white text-[10px] px-2 py-1 rounded-md whitespace-nowrap shadow-lg"
        >
          <span className="font-semibold tabular-nums">
            £{Number(pts[activeIdx].value || 0).toFixed(2)}
          </span>
          <span className="opacity-70"> · {_fmtDate(pts[activeIdx].at)}</span>
        </span>
      )}
    </span>
  );
}

// Session Z61 — Full-size cost chart overlay. Opens when any sparkline is
// clicked; annotates every change point with £ + date so an owner can
// trace the pricing story without opening the audit trail.
function CostZoomModal({ row, onClose }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  if (!row) return null;
  const points = row.cost_history || [];
  const vals = points.map((p) => Number(p.value) || 0);
  const w = 620;
  const h = 260;
  const pad = { t: 24, r: 32, b: 42, l: 56 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const min = vals.length ? Math.min(...vals) : 0;
  const max = vals.length ? Math.max(...vals) : 1;
  const padY = Math.max((max - min) * 0.15, 1);
  const yMin = Math.max(0, min - padY);
  const yMax = max + padY;
  const yRange = yMax - yMin || 1;
  const stepX = vals.length > 1 ? innerW / (vals.length - 1) : 0;
  const coords = vals.map((v, i) => {
    const x = pad.l + i * stepX;
    const y = pad.t + innerH - ((v - yMin) / yRange) * innerH;
    return [x, y];
  });
  const path = coords
    .map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(" ");
  const area = coords.length > 1
    ? `${path} L ${coords[coords.length - 1][0]} ${pad.t + innerH} L ${coords[0][0]} ${pad.t + innerH} Z`
    : "";
  const stroke = row.is_outlier ? "#dc2626" : "#0d7a75";
  const fill = row.is_outlier ? "#fee2e2" : "#ccfbf1";
  // 4 y-axis gridlines evenly spaced.
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((t) => {
    const val = yMin + t * yRange;
    const y = pad.t + innerH - t * innerH;
    return { val, y };
  });
  return (
    <div
      data-testid="cleaning-cost-zoom-dialog"
      onClick={onClose}
      className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-6 relative"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="cleaning-cost-zoom-close"
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="ns-label text-teal-700">Cost history</div>
        <div className="font-serif text-2xl mt-1 text-slate-900">{row.property_name}</div>
        <div className="text-xs text-slate-400 mt-1">
          {points.length} change point{points.length === 1 ? "" : "s"} · currently £{Number(row.cleaning_cost || 0).toFixed(2)}
          {row.is_outlier && (
            <span className="ml-2 inline-flex items-center gap-1 text-red-700">
              <AlertTriangle className="w-3 h-3" />
              {row.percent_over_avg}% above portfolio average
            </span>
          )}
        </div>
        {points.length === 0 ? (
          <div className="mt-6 text-sm text-slate-400 italic text-center py-10">
            No cost history recorded yet. Save a new cost on the matrix row to seed the first point.
          </div>
        ) : (
          <svg
            width="100%"
            viewBox={`0 0 ${w} ${h}`}
            className="mt-5"
            data-testid="cleaning-cost-zoom-chart"
          >
            {gridLines.map(({ val, y }, i) => (
              <g key={i}>
                <line
                  x1={pad.l} x2={w - pad.r} y1={y} y2={y}
                  stroke="#f1f5f9" strokeWidth="1"
                />
                <text
                  x={pad.l - 8} y={y + 4}
                  textAnchor="end"
                  fontSize="10"
                  fill="#94a3b8"
                  fontFamily="monospace"
                >
                  £{val.toFixed(0)}
                </text>
              </g>
            ))}
            {area && <path d={area} fill={fill} opacity="0.5" style={{ pointerEvents: "none" }} />}
            <path
              d={path}
              fill="none"
              stroke={stroke}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              style={{ pointerEvents: "none" }}
            />
            {coords.map(([x, y], i) => {
              const p = points[i];
              const prev = i > 0 ? Number(points[i - 1].value || 0) : null;
              const curr = Number(p.value || 0);
              const delta = prev !== null ? curr - prev : 0;
              const deltaColour = delta > 0 ? "#dc2626" : delta < 0 ? "#0d7a75" : "#64748b";
              const label = `£${curr.toFixed(0)}`;
              const dateLabel = (() => {
                try {
                  return new Date(p.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                } catch (_e) { return ""; }
              })();
              const labelY = y - 10;
              return (
                <g
                  key={i}
                  data-testid={`cleaning-cost-zoom-point-${i}`}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  style={{ cursor: "pointer" }}
                >
                  <circle cx={x} cy={y} r="4" fill="white" stroke={stroke} strokeWidth="2" />
                  {/* Wider hit target so the tooltip is forgiving on small dots. */}
                  <circle cx={x} cy={y} r="12" fill="transparent" />
                  <text
                    x={x} y={labelY}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="600"
                    fill="#0a1128"
                    fontFamily="Georgia, serif"
                  >
                    {label}
                  </text>
                  <text
                    x={x} y={pad.t + innerH + 16}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#64748b"
                  >
                    {dateLabel}
                  </text>
                  {prev !== null && delta !== 0 && (
                    <text
                      x={x} y={pad.t + innerH + 30}
                      textAnchor="middle"
                      fontSize="9"
                      fill={deltaColour}
                      fontWeight="600"
                    >
                      {delta > 0 ? "+" : ""}£{delta.toFixed(0)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        )}
        {hoverIdx !== null && points[hoverIdx] && (
          <div
            data-testid="cleaning-cost-zoom-attribution"
            className="mt-3 rounded-lg bg-slate-900 text-white text-xs px-3 py-2 flex items-center gap-3 flex-wrap"
          >
            <span className="font-semibold tabular-nums">
              £{Number(points[hoverIdx].value || 0).toFixed(2)}
            </span>
            <span className="opacity-70">
              {(() => {
                try {
                  return new Date(points[hoverIdx].at).toLocaleString("en-GB", {
                    dateStyle: "medium", timeStyle: "short",
                  });
                } catch (_e) { return points[hoverIdx].at; }
              })()}
            </span>
            <span className="opacity-70">·</span>
            <span>
              {points[hoverIdx].actor_name
                ? <>Saved by <b>{points[hoverIdx].actor_name}</b></>
                : <i className="opacity-70">Operator not recorded</i>}
            </span>
            {points[hoverIdx].action && points[hoverIdx].action !== "live" && (
              <span className="opacity-60 uppercase tracking-wider text-[10px]">
                {points[hoverIdx].action.replace("_", " ")}
              </span>
            )}
          </div>
        )}
        <div className="mt-4 text-[11px] text-slate-400 italic">
          Chart updates whenever anyone edits the cost on this row — history logged in <code>cleaning_cost_history</code>.
        </div>
      </div>
    </div>
  );
}

// Session Z57 — Cleaning cost matrix. One row per property with the assigned
// company's name and an inline cost input so owners can bulk-manage charges
// without opening each property.
export default function CleaningCostMatrix() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [edits, setEdits] = useState({}); // {property_id: new_cost}
  const [saving, setSaving] = useState(null);
  // Session Z58 — payout preview popover state.
  const [preview, setPreview] = useState(null); // {property_id, data} | null
  const [previewLoading, setPreviewLoading] = useState(false);
  // Session Z59 — portfolio stats surfaced from the API.
  const [portfolioAvg, setPortfolioAvg] = useState(0);
  const [outlierThreshold, setOutlierThreshold] = useState(0);
  // Session Z61 — zoom + email-now state.
  const [zoomRow, setZoomRow] = useState(null); // row object
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestLastRun, setDigestLastRun] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cleaning-companies/rates");
      setRows(data?.items || []);
      setPortfolioAvg(Number(data?.portfolio_avg || 0));
      setOutlierThreshold(Number(data?.outlier_threshold || 0));
      setEdits({});
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
    // Session Z61 — reflect the last Monday send on the admin UI.
    api.get("/cleaning-companies/outlier-digest/last-run")
      .then((r) => setDigestLastRun(r.data?.at || null))
      .catch(() => setDigestLastRun(null));
  };
  useEffect(() => { load(); }, []);

  // Session Z61 — manual trigger used by the "Email now" button.
  const sendDigestNow = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/cleaning-companies/outlier-digest/send-now");
      if (data?.skipped) {
        toast.info("No outliers above the 20% threshold — nothing to email.");
      } else {
        toast.success(`Digest sent to ${data?.sent ?? 0} of ${data?.recipients ?? 0} admins · ${data?.outliers ?? 0} outlier${(data?.outliers ?? 0) === 1 ? "" : "s"}.`);
      }
      load();
    } catch (err) {
      toast.error(apiError(err, "Digest failed"));
    } finally {
      setDigestBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const n = q.trim().toLowerCase();
    return rows.filter((r) =>
      (r.property_name || "").toLowerCase().includes(n) ||
      (r.cleaning_company_name || "").toLowerCase().includes(n)
    );
  }, [rows, q]);

  const saveRow = async (r) => {
    const next = edits[r.property_id];
    if (next === undefined || Number(next) === Number(r.cleaning_cost)) return;
    const parsed = Number(next);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error("Cost must be a positive number");
      return;
    }
    setSaving(r.property_id);
    try {
      await api.put(`/properties/${r.property_id}/cleaning-cost`, {
        cleaning_cost: parsed,
      });
      setRows((rs) => rs.map((x) =>
        x.property_id === r.property_id ? { ...x, cleaning_cost: parsed } : x
      ));
      setEdits((e) => { const n = { ...e }; delete n[r.property_id]; return n; });
      setPreview(null);
      toast.success(`${r.property_name} · £${parsed.toFixed(2)} saved`);
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSaving(null);
    }
  };

  // Session Z58 — opens a popover showing the 30-day projection.
  const openPreview = async (r) => {
    const nextCost = edits[r.property_id] !== undefined
      ? Number(edits[r.property_id])
      : Number(r.cleaning_cost || 0);
    if (Number.isNaN(nextCost) || nextCost < 0) {
      toast.error("Enter a valid cost first");
      return;
    }
    setPreviewLoading(true);
    setPreview({ property_id: r.property_id, data: null });
    try {
      const { data } = await api.get(
        `/properties/${r.property_id}/payout-preview?cost=${nextCost}&days=30`,
      );
      setPreview({ property_id: r.property_id, data });
    } catch (err) {
      setPreview(null);
      toast.error(apiError(err, "Preview failed"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const totals = useMemo(() => {
    const sum = filtered.reduce((a, r) => a + (r.cleaning_cost || 0), 0);
    const avg = filtered.length ? sum / filtered.length : 0;
    return { sum, avg };
  }, [filtered]);

  // Session Z60 — outlier count drives the CSV export button visibility.
  const outlierCount = useMemo(
    () => rows.filter((r) => r.is_outlier).length,
    [rows],
  );

  return (
    <section className="ns-card p-6" data-testid="cleaning-cost-matrix">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="min-w-0">
          <div className="ns-label">Payouts</div>
          <div className="font-serif text-xl mt-1">Cleaning cost per property</div>
          <div className="text-xs text-slate-400 mt-1">
            What each cleaning company charges to turn over the unit. Powers payout statements and the daily rota.
          </div>
        </div>
        <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
          <PoundSterling className="w-4 h-4" />
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="cleaning-cost-matrix-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search property or company"
            className="pl-9 h-10 bg-white border border-slate-200 rounded-md px-3 text-sm w-full"
          />
        </div>
        <div
          className="text-xs text-slate-500"
          data-testid="cleaning-cost-matrix-stats"
        >
          {filtered.length} propert{filtered.length === 1 ? "y" : "ies"} · total £{totals.sum.toFixed(2)} · avg £{totals.avg.toFixed(2)}
          {portfolioAvg > 0 && (
            <span
              className="ml-2 text-[11px] text-slate-400"
              data-testid="cleaning-cost-matrix-outlier-threshold"
            >
              · outlier &gt; £{outlierThreshold.toFixed(2)}
            </span>
          )}
        </div>
        {outlierCount > 0 && (
          <div className="inline-flex items-center gap-2">
            <a
              href="/api/cleaning-companies/outliers.csv"
              target="_blank"
              rel="noreferrer"
              data-testid="cleaning-cost-matrix-outlier-csv"
              className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-[11px] px-2.5 py-1"
              title={`Export ${outlierCount} outlier property${outlierCount === 1 ? "" : "ies"} as CSV`}
            >
              <Download className="w-3 h-3" />
              Export {outlierCount} outlier{outlierCount === 1 ? "" : "s"}
            </a>
            <button
              type="button"
              onClick={sendDigestNow}
              disabled={digestBusy}
              data-testid="cleaning-cost-matrix-digest-now"
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-teal-600 hover:text-teal-700 text-[11px] px-2.5 py-1 disabled:opacity-40"
              title={
                digestLastRun
                  ? `Last sent ${new Date(digestLastRun).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`
                  : "Fires the Monday digest ad-hoc"
              }
            >
              {digestBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              {digestBusy ? "Sending…" : "Email now"}
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto -mx-6">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-slate-500">
            <tr>
              <th className="ns-label px-6 py-3 text-left">Property</th>
              <th className="ns-label px-6 py-3 text-left">Cleaning company</th>
              <th className="ns-label px-6 py-3 text-left">6-month trend</th>
              <th className="ns-label px-6 py-3 text-right">Cost (£)</th>
              <th className="w-24 px-6 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-slate-400 italic">
                  No properties match. Add a property first, then come back to set a cleaning cost.
                </td>
              </tr>
            )}
            {filtered.map((r) => {
              const dirty = edits[r.property_id] !== undefined
                && Number(edits[r.property_id]) !== Number(r.cleaning_cost);
              const value = edits[r.property_id] !== undefined
                ? edits[r.property_id]
                : (r.cleaning_cost || 0);
              return (
                <tr
                  key={r.property_id}
                  data-testid={`cleaning-cost-row-${r.property_id}`}
                  className="border-t border-slate-100 hover:bg-stone-50/40"
                >
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Home className="w-3.5 h-3.5 text-teal-700 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">{r.property_name}</div>
                        {r.bedrooms != null && (
                          <div className="text-[11px] text-slate-400">{r.bedrooms} bed</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    {r.cleaning_company_name ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-700">{r.cleaning_company_name}</span>
                        {r.cleaning_company_active === false && (
                          <span className="text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
                            inactive
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs italic text-slate-400">Not assigned</span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <button
                      type="button"
                      onClick={() => setZoomRow(r)}
                      data-testid={`cleaning-cost-sparkline-${r.property_id}`}
                      className="inline-flex items-center gap-2 group hover:opacity-80 cursor-zoom-in"
                      title={
                        (r.cost_history || []).length > 1
                          ? `Click to zoom · ${r.cost_history.length} change${r.cost_history.length === 1 ? "" : "s"} in the last 6 months`
                          : "Click to zoom · no cost changes yet"
                      }
                    >
                      <Sparkline
                        points={r.cost_history}
                        outlier={r.is_outlier}
                        testid={`cleaning-cost-sparkline-${r.property_id}`}
                      />
                      <span className="text-[10px] text-slate-400 tabular-nums group-hover:text-teal-700">
                        {(r.cost_history || []).length} pt{(r.cost_history || []).length === 1 ? "" : "s"}
                      </span>
                    </button>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      {r.is_outlier && (
                        <span
                          data-testid={`cleaning-cost-outlier-${r.property_id}`}
                          title={`${r.percent_over_avg}% above the portfolio average of £${portfolioAvg.toFixed(2)}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5"
                        >
                          <AlertTriangle className="w-3 h-3" />
                          +{r.percent_over_avg}%
                        </span>
                      )}
                      <div className="inline-flex items-center gap-1">
                        <span className="text-slate-400">£</span>
                        <input
                          data-testid={`cleaning-cost-input-${r.property_id}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={value}
                          onChange={(e) => setEdits((x) => ({
                            ...x, [r.property_id]: e.target.value,
                          }))}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRow(r); }}
                          className={`w-24 h-9 text-right rounded-md border px-2 text-sm bg-white tabular-nums ${
                            dirty ? "border-amber-300 ring-1 ring-amber-100" : "border-slate-200"
                          }`}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        data-testid={`cleaning-cost-preview-${r.property_id}`}
                        onClick={() => openPreview(r)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-teal-600 hover:text-teal-700 px-3 py-1 text-[11px]"
                        title="See the 30-day payout impact of this cost"
                      >
                        <LineChart className="w-3 h-3" />
                        Preview payout
                      </button>
                      {dirty && (
                        <button
                          type="button"
                          data-testid={`cleaning-cost-save-${r.property_id}`}
                          onClick={() => saveRow(r)}
                          disabled={saving === r.property_id}
                          className="inline-flex items-center gap-1 rounded-full ns-btn-primary px-3 py-1 text-[11px]"
                        >
                          <Save className="w-3 h-3" />
                          {saving === r.property_id ? "Saving…" : "Save"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {preview && (
        <div
          data-testid="cleaning-cost-preview-dialog"
          onClick={() => setPreview(null)}
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 relative"
          >
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            {previewLoading || !preview.data ? (
              <div className="text-sm text-slate-500 py-10 text-center italic">
                Working out the projection…
              </div>
            ) : (
              <>
                <div className="ns-label text-teal-700">30-day projection</div>
                <div className="font-serif text-2xl mt-1 text-slate-900">
                  {preview.data.property_name}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {preview.data.start} → {preview.data.end}
                </div>
                <div className="mt-5 rounded-xl border border-slate-100 bg-stone-50/60 p-5">
                  <div className="text-sm text-slate-700 leading-relaxed">
                    Based on{" "}
                    <b data-testid="cleaning-cost-preview-turnovers">
                      {preview.data.turnovers}
                    </b>{" "}
                    turnover{preview.data.turnovers === 1 ? "" : "s"} ×{" "}
                    <b>£{preview.data.cost_per_turnover.toFixed(2)}</b>
                    {" "}={" "}
                    <b
                      data-testid="cleaning-cost-preview-total"
                      className="text-teal-700"
                    >
                      £{preview.data.projected_payout.toFixed(2)}
                    </b>
                    {preview.data.cleaning_company_name && (
                      <> payable to {preview.data.cleaning_company_name}</>
                    )}.
                  </div>
                  {preview.data.turnovers === 0 && (
                    <div className="text-[11px] text-amber-700 mt-3">
                      No turnovers scheduled in this window — the 30-day window stays flat.
                    </div>
                  )}
                  {preview.data.cleaning_company_active === false && (
                    <div className="text-[11px] text-amber-700 mt-3">
                      This company is currently inactive — reactivate it in Settings before the next rota blast.
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-slate-400 mt-4 italic">
                  Projection uses the <b>unsaved</b> value in the Cost input — close this
                  popover and tap Save to commit the change.
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <CostZoomModal row={zoomRow} onClose={() => setZoomRow(null)} />
    </section>
  );
}
